import * as React from "react";

interface IBodyStyle {
  overflow: string;
  position: string;
  top: string;
  width: string;
  height: string;
  paddingRight: string;
}

let lockCount = 0;
let lockedScrollY = 0;
let previousBodyStyle: IBodyStyle | null = null;
let previousRootStyle: { overflow: string; height: string } | null = null;

/**
 * Pin the page behind a modal surface and restore it on release.
 *
 * Both elements, and both properties. iOS Safari will scroll the page to reveal
 * a focused input whatever a single one of them says, and the scroll is what
 * makes the visual and layout viewports diverge — after which a fixed panel is
 * painted away from where it is laid out, opening a strip of page below a drawer
 * that no element in the page can cover, because in layout terms the strip is
 * not there. So the scroll has to be prevented rather than papered over.
 *
 * Ref-counted: a nested surface closing must not release the lock the one
 * underneath still needs.
 */
export const useBodyScrollLock = (active: boolean): void => {
  React.useEffect(() => {
    if (!active) return;

    lockCount += 1;

    if (lockCount === 1) {
      const { body, documentElement: root } = document;
      lockedScrollY = window.scrollY;
      // documentElement, not just body: on iOS the scrolling element is html, so
      // pinning body alone leaves the document free to scroll — measured
      // scrollY=636 with body already position:fixed. That scroll is what offsets
      // the visual viewport from the layout viewport (measured vvTop=318), and
      // once those diverge a fixed panel is drawn away from where it is laid out.
      // Nothing inside the page can cover the strip that opens up, because in
      // layout terms the strip does not exist.
      previousRootStyle = {
        overflow: root.style.overflow,
        height: root.style.height,
      };
      root.style.overflow = "hidden";
      root.style.height = "100%";
      previousBodyStyle = {
        overflow: body.style.overflow,
        position: body.style.position,
        top: body.style.top,
        width: body.style.width,
        height: body.style.height,
        paddingRight: body.style.paddingRight,
      };
      // Taking the page out of flow removes the scrollbar where one is drawn
      // in the layout (desktop), which would shift the content sideways.
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.top = `-${lockedScrollY}px`;
      body.style.width = "100%";
      // Bound the body to the viewport so `overflow: hidden` has something to
      // clip: without a height it stays as tall as its content, and iOS reveals
      // the part below the viewport when the keyboard shifts things — page
      // content appearing in the strip under an open drawer, which nothing
      // inside the page can paint over. The scroll offset is added back because
      // `top` has already shifted the box up by it; 100% alone would end short
      // of the viewport bottom and clip content that should be visible.
      body.style.height = `calc(100% + ${lockedScrollY}px)`;
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    return () => {
      lockCount -= 1;

      if (lockCount > 0 || !previousBodyStyle) return;

      const { body, documentElement: root } = document;
      if (previousRootStyle) {
        root.style.overflow = previousRootStyle.overflow;
        root.style.height = previousRootStyle.height;
        previousRootStyle = null;
      }
      body.style.overflow = previousBodyStyle.overflow;
      body.style.position = previousBodyStyle.position;
      body.style.top = previousBodyStyle.top;
      body.style.width = previousBodyStyle.width;
      body.style.height = previousBodyStyle.height;
      body.style.paddingRight = previousBodyStyle.paddingRight;
      previousBodyStyle = null;
      window.scrollTo(0, lockedScrollY);
    };
  }, [active]);
};
